
import ProfilePageWallCardSkeleton from '@@/library/v4/components/ProfilePageWallCardSkeleton.vue'
import { browserCan } from 'bits/browser'
import currentUser from 'bits/current_user'
import device from 'bits/device'
import { isAppUsing } from 'bits/flip'
import window, { ProfilePage as ProfilePageType, ProfileTarget } from 'bits/global'
import { navigateTo } from 'bits/location'
import { openNativeShare } from 'bits/native_share'
import { isRegistered } from 'bits/user_model'
import { Component, Vue, namespace } from 'bits/vue'
import { SnackbarNotificationType } from 'enums'
import OzAvatar from 'library/v4/components/OzAvatar.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from 'library/v4/components/OzContainedButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzIconButton, { OzIconButtonColorScheme } from 'library/v4/components/OzIconButton.vue'
import OzIndeterminateSpinner from 'library/v4/components/OzIndeterminateSpinner.vue'
import OzWallCard from 'library/v4/components/OzWallCard.vue'
import OzWallListRow, { OzListRowSeparator } from 'library/v4/components/OzWallListRow.vue'
import { bridgeUrlClick } from 'native_bridge/bridge_url'
import { AccountKey, User, Wall } from 'types'
import { CollectionKey } from 'types/collections'
import FollowButton from 'vuecomponents/FollowButton.vue'
import GlobalConfirmationDialog from 'vuecomponents/GlobalConfirmationDialog.vue'
import LandsideFooter from 'vuecomponents/LandsideFooter.vue'
import MembershipTierUpgradeModal from 'vuecomponents/MembershipTierUpgradeModal.vue'
import UserMenu from 'vuecomponents/UserMenu.vue'
import ProgressIndeterminate from 'vuecomponents/progress_indeterminate.vue'
import { useCopyToClipboard } from 'vuecomposables/copy_to_clipboard'
import DarkModeMixin from 'vuemixins/dark_mode_mixin'
import WindowSizeMixin from 'vuemixins/window_size_mixin'
import { UpgradeSource } from 'vuexstore/modules/plans'
import AppBarAccountAvatar from './AppBarAccountAvatar.vue'
import DashWallCardList from './DashWallCardList.vue'
import GlobalSnackbar from './GlobalSnackbar.vue'
import PadletLogo, { LogoType } from './PadletLogo.vue'

enum CardWidth {
  Desktop = 256,
  TabletLandscape = 232,
  TabletPortrait = 228,
}

const appBarAccountStore = namespace('appBarAccount')
const plansStore = namespace('plans')
const wallCollectionsStore = namespace('wallCollections')
const browserStore = namespace('browser')
const globalSnackbarStore = namespace('globalSnackbar')

@Component({
  components: {
    UserMenu,
    OzIndeterminateSpinner,
    GlobalConfirmationDialog,
    MembershipTierUpgradeModal,
    DashWallCardList,
    ProgressIndeterminate,
    OzIconButton,
    GlobalSnackbar,
    PadletLogo,
    AppBarAccountAvatar,
    OzContainedButton,
    FollowButton,
    OzIcon,
    OzWallCard,
    ProfilePageWallCardSkeleton,
    OzAvatar,
    OzWallListRow,
    LandsideFooter,
  },
  mixins: [DarkModeMixin, WindowSizeMixin],
  setup() {
    const { copyToClipboard } = useCopyToClipboard()
    return { copyToClipboard }
  },
})
export default class ProfilePage extends Vue {
  copyToClipboard!: ReturnType<typeof useCopyToClipboard>['copyToClipboard']

  profileTarget = window.$profileTarget as ProfileTarget
  profilePage = window.$profilePage as ProfilePageType
  currentUser = currentUser
  UpgradeSource = UpgradeSource
  OzIconButtonColorScheme = OzIconButtonColorScheme
  OzContainedButtonColorScheme = OzContainedButtonColorScheme
  OzContainedButtonSizePreset = OzContainedButtonSizePreset

  @globalSnackbarStore.Action setSnackbar!: (args: {
    message: string
    notificationType: SnackbarNotificationType
    timeout: number
  }) => void

  isAppUsing = isAppUsing

  @browserStore.Getter isDarkmode!: boolean
  @browserStore.Getter isSmallerThanTabletPortrait!: boolean
  @browserStore.Getter isSmallerThanTabletLandscape!: boolean
  @browserStore.Getter isSmallerThanDesktop!: boolean
  @browserStore.Getter isAndroid!: boolean
  @browserStore.Getter isMobile!: boolean
  @browserStore.Getter isTablet!: boolean

  @plansStore.Getter xUpgradeModal!: boolean
  @plansStore.Action userTriggeredUpgrade!: (args: { user: User; upgradeSource: UpgradeSource }) => void
  @plansStore.Action closePlanUpgradeModal!: () => void

  @appBarAccountStore.Getter xUserMenu!: boolean

  @wallCollectionsStore.Getter activeCollectionWalls!: Wall[]
  @wallCollectionsStore.Getter isActiveCollectionFetching!: boolean
  @wallCollectionsStore.Action fetchActiveCollectionWallsNow!: () => void
  @wallCollectionsStore.Action setAccountCollectionCursor!: (args: {
    collectionKey: CollectionKey
    accountKey: AccountKey
  }) => void

  get isApp(): boolean {
    return device.app || device.electronApp
  }

  get copyLinkIconName(): string {
    if (!this.canBrowserShare) {
      return 'link'
    }
    return this.isAndroid ? 'share_android_outline' : 'share_outline'
  }

  get canBrowserShare(): boolean {
    return browserCan('share') && (device.mobile || device.tablet)
  }

  get cardWidth(): number {
    if (this.isSmallerThanTabletLandscape) {
      return CardWidth.TabletPortrait
    }
    if (this.isSmallerThanDesktop) {
      return CardWidth.TabletLandscape
    }
    return CardWidth.Desktop
  }

  get displayName(): string {
    return this.profileTarget.displayName || this.profileTarget.name
  }

  get isPayingProfile(): boolean {
    return this.profilePage.isPayingProfile
  }

  get isUserProfile(): boolean {
    return this.profileTarget.type === 'user'
  }

  get isOwnProfile(): boolean {
    return this.currentUser.id === this.profileTarget.id
  }

  // Team profiles can be edited by admin/owner roles even though it's not their own profile
  get canIEditProfile(): boolean {
    return this.profilePage.canIEditProfile
  }

  get isUserRegistered(): boolean {
    return isRegistered(this.currentUser)
  }

  get padletLogoType(): LogoType {
    return this.isDarkmode ? LogoType.Light300 : LogoType.Dark300
  }

  get noWallsMessage(): string {
    return this.__('%{name} has nothing public to show.', {
      name: this.profileTarget.displayName || this.profileTarget.name,
    })
  }

  get isEmpty(): boolean {
    return this.activeCollectionWalls.length === 0
  }

  get numberOfPlaceholderCardsForSingleRow(): number {
    const MAX_CARDS_FOR_SINGLE_ROW_DESKTOP = 4
    const MAX_CARDS_FOR_SINGLE_ROW_TABLET_PORTRAIT = 3

    const maxPlaceholderCards = this.isSmallerThanTabletLandscape
      ? MAX_CARDS_FOR_SINGLE_ROW_TABLET_PORTRAIT
      : MAX_CARDS_FOR_SINGLE_ROW_DESKTOP

    const activeWallsCount = this.activeCollectionWalls.length
    return Math.max(0, maxPlaceholderCards - activeWallsCount)
  }

  copyLink() {
    const link = window.location.href
    if (this.canBrowserShare) {
      openNativeShare({ url: link })
    } else {
      this.copyToClipboard({
        text: link,
        globalSnackbarOptions: {
          message: this.__('Profile link copied to clipboard'),
          notificationType: SnackbarNotificationType.success,
          timeout: 1500,
        },
      })
    }
  }

  ozWallListRowSeparator(index: number): OzListRowSeparator {
    const isLastRowInList = index === this.activeCollectionWalls.length - 1
    return isLastRowInList ? OzListRowSeparator.None : OzListRowSeparator.Short
  }

  showWall(wall: Wall): void {
    navigateTo(wall.links?.show || wall.address)
  }

  editProfile(event: Event): void {
    bridgeUrlClick(event, this.profilePage.editProfileUrl, 'internal')
  }

  mounted(): void {
    this.setAccountCollectionCursor({
      collectionKey: {
        typeKey: 'filter',
        indexKey: this.isUserProfile ? 'profile' : 'all',
      },
      accountKey: {
        type: this.isUserProfile ? 'user' : 'library',
        id: this.profileTarget.id,
      },
    })
    this.fetchActiveCollectionWallsNow()
  }
}
